import type { ApiPayload } from "@Front/Api/types";

export type { FolderSharingPayload, InvitedNotaryEmailPayload } from "./businessRequestFolderSharingPayloadHelpers";
export { buildFolderSharingPayload, buildInvitedNotaryEmailPayload } from "./businessRequestFolderSharingPayloadHelpers";

export interface DocumentsBatchPayload<TDocument> {
	documents: TDocument[];
}

export interface RefusedReasonPayload {
	refused_reason: string;
}

export interface DocumentUidsPayload {
	documentUids: string[];
}

export interface DocumentsUidPayload {
	documentsUid: string[];
}

export interface ReminderConfigPayload {
	reminderIntervals?: number[];
	reminderMessage?: string;
	autoSend?: boolean;
	isActive?: boolean;
}

export interface SetActiveOfficePayload {
	office_uid: string;
	/** Bureau métier pour le JWT (Espace office) lorsque `office_uid` est `GUEST_FOLDERS`. */
	menu_office_uid?: string;
}

export interface FolderArchivePayload {
	archived_description: string;
}

export interface DeletePayloadByUid {
	uid: string;
}

export interface LiveVotesDeletePayload {
	uid: string;
}

export interface FilesDeletePayload {
	uid: string;
}

/**
 * Transversal convention for delete operations that take a uid (path or body).
 * Use this generic builder to centralize uid validation; domains can expose
 * thin wrappers (e.g. buildLiveVotesDeletePayload) for typing.
 */
export function buildDeletePayloadByUid(uid: string | null | undefined): DeletePayloadByUid {
	if (typeof uid !== "string" || uid.trim().length === 0) {
		throw new Error("Delete payload requires a non-empty uid");
	}
	return { uid: uid.trim() };
}

/** Domain wrapper: delete payload for liveVotes (uses generic buildDeletePayloadByUid). */
export function buildLiveVotesDeletePayload(uid: string | null | undefined): LiveVotesDeletePayload {
	return buildDeletePayloadByUid(uid);
}

/** Domain wrapper: delete payload for files (uses generic buildDeletePayloadByUid). */
export function buildFilesDeletePayload(uid: string | null | undefined): FilesDeletePayload {
	return buildDeletePayloadByUid(uid);
}

export function buildOfficeRolesCreatePayload<TOfficeRolePayload extends object>(
	payload: TOfficeRolePayload,
): TOfficeRolePayload {
	return payload;
}

export function buildOfficeRolesUpdatePayload<TOfficeRolePayload extends object>(
	payload: TOfficeRolePayload,
): TOfficeRolePayload {
	return payload;
}

export function buildStripeCreatePayload<TStripePayload extends object>(payload: TStripePayload): TStripePayload {
	return payload;
}

export function buildFilesUpdatePayload<TFilePayload extends object>(payload: TFilePayload): TFilePayload {
	return payload;
}

export function buildNotificationsUpdatePayload<TNotificationPayload extends object>(
	payload: TNotificationPayload,
): TNotificationPayload {
	return payload;
}

export function buildHealthTestPayload<THealthPayload extends object>(payload: THealthPayload): THealthPayload {
	return payload;
}

export function buildLiveVotesCreatePayload<TLiveVotePayload extends object>(payload: TLiveVotePayload): TLiveVotePayload {
	return payload;
}

export function buildDeedsCreatePayload<TDeedPayload extends object>(payload: TDeedPayload): TDeedPayload {
	return payload;
}

export function buildDeedsUpdatePayload<TDeedPayload extends object>(payload: TDeedPayload): TDeedPayload {
	return payload;
}

export function buildDeedTypesCreatePayload<TDeedTypePayload extends object>(
	payload: TDeedTypePayload,
): TDeedTypePayload {
	return payload;
}

export function buildDeedTypesUpdatePayload<TDeedTypePayload extends object>(
	payload: TDeedTypePayload,
): TDeedTypePayload {
	return payload;
}

export function buildDocumentTypesCreatePayload<TDocumentTypePayload extends object>(
	payload: TDocumentTypePayload,
): TDocumentTypePayload {
	return payload;
}

export function buildDocumentTypesUpdatePayload<TDocumentTypePayload extends object>(
	payload: TDocumentTypePayload,
): TDocumentTypePayload {
	return payload;
}

export function buildRolesCreatePayload<TRolePayload extends object>(payload: TRolePayload): TRolePayload {
	return payload;
}

export function buildRolesUpdatePayload<TRolePayload extends object>(payload: TRolePayload): TRolePayload {
	return payload;
}

export function buildCustomersCreatePayload<TCustomerPayload extends object>(payload: TCustomerPayload): TCustomerPayload {
	return payload;
}

export function buildCustomersUpdatePayload<TCustomerPayload extends object>(payload: TCustomerPayload): TCustomerPayload {
	return payload;
}

export function buildUsersCreatePayload<TUserPayload extends object>(payload: TUserPayload): TUserPayload {
	return payload;
}

export function buildUsersUpdatePayload<TUserPayload extends object>(payload: TUserPayload): TUserPayload {
	return payload;
}

export function buildEmptyPayload(): Record<string, never> {
	return {};
}

export function buildDocumentsCreatePayload<TDocumentPayload extends object>(payload: TDocumentPayload): TDocumentPayload {
	return payload;
}

export function buildDocumentsUpdatePayload<TDocumentPayload extends object>(payload: TDocumentPayload): TDocumentPayload {
	return payload;
}

export function buildFoldersCreatePayload<TFolderPayload extends object>(payload: TFolderPayload): TFolderPayload {
	return payload;
}

export function buildFoldersUpdatePayload<TFolderPayload extends object>(payload: TFolderPayload): TFolderPayload {
	return payload;
}

export function buildFolderArchivePayload(archivedDescription: string): FolderArchivePayload {
	return {
		archived_description: archivedDescription,
	};
}

export function buildDocumentsBatchPayload<TDocument>(documents: TDocument[]): DocumentsBatchPayload<TDocument> {
	return { documents };
}

export function buildRefusedReasonPayload(refusedReason: string): RefusedReasonPayload {
	return { refused_reason: refusedReason };
}

export function buildDocumentUidsPayload(documentUids: string[]): DocumentUidsPayload {
	return { documentUids };
}

export function buildDocumentsUidPayload(documentsUid: string[]): DocumentsUidPayload {
	return { documentsUid };
}

export function buildReminderConfigPayload(
	reminderIntervals?: number[],
	reminderMessage?: string,
	autoSend?: boolean,
	isActive?: boolean,
): ReminderConfigPayload {
	return {
		reminderIntervals,
		reminderMessage,
		autoSend,
		isActive,
	};
}

export function buildSetActiveOfficePayload(officeUid: string, menuOfficeUidForGuestJwt?: string | null): SetActiveOfficePayload {
	const base: SetActiveOfficePayload = { office_uid: officeUid };
	const trimmed = menuOfficeUidForGuestJwt?.trim();
	if (officeUid === "GUEST_FOLDERS" && trimmed && trimmed !== "GUEST_FOLDERS") {
		return { ...base, menu_office_uid: trimmed };
	}
	return base;
}

export function toApiPayload<TPayload extends object>(payload: TPayload): ApiPayload {
	return payload as unknown as ApiPayload;
}

export function buildPayloadOrEmpty(payload?: ApiPayload): ApiPayload {
	return payload ?? buildEmptyPayload();
}

export function buildStatusUpdatePayload<TPayload extends object, TStatus>(
	payload: TPayload,
	status: TStatus,
): TPayload & { status: TStatus } {
	return {
		...payload,
		status,
	};
}
